import { LineReveal, FadeUp } from "../components/RevealText";
import ServiceRequestForm from "../components/ServiceRequestForm";
import ServiceCarousel from "../components/ServiceCarousel";

const CAPABILITIES = [
  "Emergency HVAC service available 24 hours a day, 365 days a year",
  "Customized preventative maintenance programs",
  "Commercial heating and air-conditioning service and repairs",
  "Chiller, boiler, rooftop unit, split-system, VRF, and central-plant service",
  "Equipment troubleshooting and system diagnostics",
  "Refrigerant leak detection, repairs, and system replacements",
  "Steam-fitting and hydronic piping services",
  "HVAC equipment replacements, upgrades, and retrofits",
  "Building surveys and equipment-condition assessments",
  "System startup, testing, and commissioning",
  "Building management and control-system troubleshooting and coordination",
  "Complete HVAC system design and mechanical-system modifications",
  "Drafting, layout, and full field installation",
  "Sheet-metal layout and CAD-based coordination drawings",
  "In-house sheet-metal fabrication",
];

const SERVICE_AREA = [
  "New York City — Manhattan, Brooklyn, Queens, the Bronx, and Staten Island",
  "Nassau County",
  "Suffolk County",
  "Westchester County",
];

const UNIONS = [
  "Local 28 Sheet Metal Workers",
  "Local 638 “B” Service Mechanics",
];

const ACCOUNTS = [
  {
    heading: "Healthcare, Education and Institutional",
    items: [
      "Northwell Health — Multiple Hospitals and Off-Site Facilities",
      "Catholic Health — Various Off-Site Facilities",
      "EmblemHealth",
      "St. Joseph Hospital",
      "Good Samaritan University Hospital",
      "Mather Hospital",
      "St. Charles Hospital",
      "Stony Brook University",
      "Stony Brook University Hospital",
      "Cold Spring Harbor Laboratory",
      "Suffolk Community Hospital",
      "Huntington Union Free School District",
      "Hampton Bays Union Free School District",
      "Nassau County Family Court",
      "Nassau County Correctional Center",
      "Suffolk County Department of Public Works",
      "Old Westbury Gardens",
    ],
  },
  {
    heading: "Banking and Financial Services",
    items: [
      "JPMorgan Chase",
      "Webster Bank",
      "Dime Community Bank",
      "Bethpage Federal Credit Union",
      "Teachers Federal Credit Union",
      "FourLeaf Federal Credit Union",
      "Flagstar Bank",
      "Sterling National Bank",
      "BNB Bank",
      "Bank of New York",
      "Astoria Bank",
      "Beal Bank",
      "Bank of America",
    ],
  },
  {
    heading: "Commercial, Corporate and Industrial",
    items: [
      "National Grid — Multiple Locations",
      "Forge River Plant",
      "NBC",
      "LiRo Group",
      "Albanese Organization",
      "Schmergel Enterprises",
      "Steel Equities",
      "Nikon",
      "Leviton Corporate Headquarters",
      "Dealertrack",
      "United Rentals",
      "Tully Construction",
      "Fortunato & Sons Contracting — Multiple Locations",
      "Sysco Foods",
      "Estée Lauder",
      "Blue Point Brewing Company",
    ],
  },
  {
    heading: "Residential, Hospitality and Property Management",
    items: [
      "GAZ Realty",
      "Rafferty Holdings",
      "Hammes",
      "Sunrise Senior Living — Multiple Locations",
      "Hendrick’s Restaurant",
      "Ronkonkoma HUB",
      "Wyandanch Rising",
      "Greybarn Apartments",
      "TWA Hotel",
      "Damianos Realty Group",
    ],
  },
  {
    heading: "Retail, Entertainment and Aviation",
    items: [
      "Starbucks — Multiple Locations",
      "Target — Multiple Locations",
      "Macy’s",
      "Prada",
      "Live Nation at Jones Beach Theater",
      "JFK Airport Plaza",
    ],
  },
];

export default function Service() {
  return (
    <main data-testid="service-page" className="bg-black text-white pt-24">
      {/* Header */}
      <section className="border-b border-white/10 px-4 md:px-6 py-12 md:py-16">
        <h1 className="font-display font-bold text-6xl md:text-8xl leading-[0.9] uppercase">
          <LineReveal delay={0.15}>Service</LineReveal>
        </h1>
      </section>

      {/* Hero Field Reel — auto-advancing carousel */}
      <ServiceCarousel />

      {/* Doc content */}
      <section
        data-testid="service-body"
        className="border-b border-white/10 px-4 md:px-6 py-14 md:py-20"
      >
        <div className="grid grid-cols-12 gap-6">
          <div className="col-span-12 md:col-span-10 md:col-start-2 space-y-14">
            {/* Intro */}
            <FadeUp>
              <h2 className="font-display font-bold text-4xl md:text-6xl leading-[0.95] uppercase mb-4">
                Anron Mechanical Services, Inc.
              </h2>
              <p className="font-mono text-xs uppercase tracking-[0.25em] text-white/50 mb-8">
                Commercial HVAC Service &amp; Mechanical Solutions Since 1965
              </p>
              <div className="space-y-6 text-white/75 text-lg leading-relaxed">
                <p>
                  Founded in 1965 and privately owned and operated, Anron
                  Mechanical Services, Inc. is one of Metropolitan New York and
                  Long Island’s most established and respected full-service
                  commercial HVAC organizations. For more than 60 years, Anron
                  has provided dependable HVAC service, maintenance, repair,
                  replacement, and installation solutions to commercial,
                  institutional, healthcare, industrial, and residential
                  facilities.
                </p>
                <p>
                  Our experienced service division supports office buildings,
                  corporate headquarters, hospitals, healthcare facilities,
                  schools, assisted-living communities, laboratories,
                  correctional facilities, financial institutions, retail
                  centers, hotels, multifamily properties, industrial
                  facilities, and other mission-critical environments.
                </p>
              </div>
            </FadeUp>

            {/* Capabilities */}
            <FadeUp>
              <h3 className="font-display font-bold text-3xl md:text-4xl uppercase mb-6">
                HVAC Service Capabilities
              </h3>
              <ul className="grid grid-cols-1 md:grid-cols-2 gap-x-8 border-t border-white/10">
                {CAPABILITIES.map((c) => (
                  <li
                    key={c}
                    className="flex items-start gap-4 py-3 border-b border-white/10 text-white/80 text-base"
                  >
                    <span className="font-mono text-[10px] text-white/40 pt-1">
                      ▸
                    </span>
                    <span>{c}</span>
                  </li>
                ))}
              </ul>
              <div className="mt-8 space-y-6 text-white/75 text-lg leading-relaxed">
                <p>
                  Anron’s fully equipped service division is staffed by
                  experienced Local 638 “B” service mechanics and supported by
                  our Local 28 sheet-metal workforce, project-management team,
                  and in-house fabrication facility.
                </p>
                <p>
                  Our automated fabrication shop includes computer-controlled
                  plasma cutting, automated coil-line production, spiral duct
                  fabrication, and welding capabilities. This vertically
                  integrated structure allows Anron to respond quickly, control
                  project scheduling, and provide complete solutions from
                  initial diagnosis through fabrication, installation, startup,
                  and ongoing maintenance.
                </p>
              </div>
            </FadeUp>

            {/* Service Area + Unions */}
            <FadeUp>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-10 md:gap-14">
                <div>
                  <h3 className="font-display font-bold text-2xl md:text-3xl uppercase mb-4">
                    Service Area
                  </h3>
                  <ul className="border-t border-white/10">
                    {SERVICE_AREA.map((s) => (
                      <li
                        key={s}
                        className="py-3 border-b border-white/10 text-white/80 text-base"
                      >
                        {s}
                      </li>
                    ))}
                  </ul>
                </div>
                <div>
                  <h3 className="font-display font-bold text-2xl md:text-3xl uppercase mb-4">
                    Union Affiliations
                  </h3>
                  <ul className="border-t border-white/10">
                    {UNIONS.map((u) => (
                      <li
                        key={u}
                        className="py-3 border-b border-white/10 text-white/80 text-base"
                      >
                        {u}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </FadeUp>

            {/* Selected Accounts */}
            <FadeUp>
              <h3 className="font-display font-bold text-3xl md:text-4xl uppercase mb-8">
                Selected Past &amp; Present Service Accounts
              </h3>
              <div className="space-y-12">
                {ACCOUNTS.map((group) => (
                  <div key={group.heading}>
                    <p className="font-mono text-xs uppercase tracking-[0.25em] text-white/50 mb-4">
                      {group.heading}
                    </p>
                    <ul className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-x-8 border-t border-white/10">
                      {group.items.map((item) => (
                        <li
                          key={item}
                          className="flex items-start gap-3 py-2.5 border-b border-white/10 text-white/80 text-sm md:text-base"
                        >
                          <span>{item}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                ))}
              </div>
            </FadeUp>
          </div>
        </div>
      </section>

      <ServiceRequestForm />
    </main>
  );
}
